import type {
  Campaign,
  CampaignOperationalStatus,
  CampaignOperationalSuspendedReason,
} from '../types';

export interface CampaignDisplayStatusState {
  status: CampaignOperationalStatus;
  suspendedReason: CampaignOperationalSuspendedReason | null;
}

export const CAMPAIGN_SUSPENDED_REASON_LABEL_KEYS: Record<CampaignOperationalSuspendedReason, string> = {
  outside_schedule: 'pages:campaigns.suspendedReasons.outsideSchedule.label',
  ai_balance_unavailable: 'pages:campaigns.suspendedReasons.aiBalanceUnavailable.label',
  telephony_balance_unavailable: 'pages:campaigns.suspendedReasons.telephonyBalanceUnavailable.label',
  telephony_overage_pending: 'pages:campaigns.suspendedReasons.telephonyOveragePending.label',
  transfer_operator_unavailable: 'pages:campaigns.suspendedReasons.transferOperatorUnavailable.label',
};

export const CAMPAIGN_SUSPENDED_REASON_TOOLTIP_KEYS: Record<CampaignOperationalSuspendedReason, string> = {
  outside_schedule: 'pages:campaigns.suspendedReasons.outsideSchedule.tooltip',
  ai_balance_unavailable: 'pages:campaigns.suspendedReasons.aiBalanceUnavailable.tooltip',
  telephony_balance_unavailable: 'pages:campaigns.suspendedReasons.telephonyBalanceUnavailable.tooltip',
  telephony_overage_pending: 'pages:campaigns.suspendedReasons.telephonyOveragePending.tooltip',
  transfer_operator_unavailable: 'pages:campaigns.suspendedReasons.transferOperatorUnavailable.tooltip',
};

export const CAMPAIGN_DETAIL_SUSPENDED_REASON_TITLE_KEYS: Record<CampaignOperationalSuspendedReason, string> = {
  outside_schedule: 'pages:campaigns.detail.suspendedReasons.outsideSchedule.title',
  ai_balance_unavailable: 'pages:campaigns.detail.suspendedReasons.aiBalanceUnavailable.title',
  telephony_balance_unavailable: 'pages:campaigns.detail.suspendedReasons.telephonyBalanceUnavailable.title',
  telephony_overage_pending: 'pages:campaigns.detail.suspendedReasons.telephonyOveragePending.title',
  transfer_operator_unavailable: 'pages:campaigns.detail.suspendedReasons.transferOperatorUnavailable.title',
};

export const CAMPAIGN_DETAIL_SUSPENDED_REASON_MESSAGE_KEYS: Record<CampaignOperationalSuspendedReason, string> = {
  outside_schedule: 'pages:campaigns.detail.suspendedReasons.outsideSchedule.message',
  ai_balance_unavailable: 'pages:campaigns.detail.suspendedReasons.aiBalanceUnavailable.message',
  telephony_balance_unavailable: 'pages:campaigns.detail.suspendedReasons.telephonyBalanceUnavailable.message',
  telephony_overage_pending: 'pages:campaigns.detail.suspendedReasons.telephonyOveragePending.message',
  transfer_operator_unavailable: 'pages:campaigns.detail.suspendedReasons.transferOperatorUnavailable.message',
};

export const CAMPAIGN_TRANSFER_TARGET_FALLBACK_KEY =
  'pages:campaigns.suspendedReasons.transferOperatorUnavailable.targetFallback';

interface CampaignSuspendedReasonOptionsSource {
  transferTargetName?: string | null;
}

/**
 * Build the shared interpolation options for campaign suspended reason copy.
 *
 * Transfer-operator suspension is target-specific, so every surface that shows
 * this reason should pass the configured target name when it is available.
 */
export function buildCampaignSuspendedReasonI18nOptions(
  campaign: CampaignSuspendedReasonOptionsSource | null | undefined,
  options: {
    nextRun: string;
    transferTargetFallback: string;
  },
) {
  const { nextRun, transferTargetFallback } = options;
  return {
    nextRun,
    transferTargetName: campaign?.transferTargetName || transferTargetFallback,
  };
}

/**
 * Resolve the display-only campaign status exposed in the UI.
 *
 * The backend now emits `operationalStatus` and `operationalStatusReason`, but
 * this helper keeps a safe fallback for partial deploys where older payloads
 * still only expose raw `status` plus `isWithinSchedule`.
 */
export function getCampaignDisplayStatusState(
  campaign: Pick<
    Campaign,
    'status' | 'operationalStatus' | 'operationalStatusReason' | 'isWithinSchedule'
  > | null | undefined,
): CampaignDisplayStatusState {
  if (!campaign) {
    return {
      status: 'draft',
      suspendedReason: null,
    };
  }

  if (campaign.operationalStatus) {
    return {
      status: campaign.operationalStatus,
      suspendedReason: campaign.operationalStatusReason ?? null,
    };
  }

  const isOutsideSchedule = campaign.status === 'active' && campaign.isWithinSchedule === false;
  if (isOutsideSchedule) {
    return {
      status: 'suspended',
      suspendedReason: 'outside_schedule',
    };
  }

  return {
    status: campaign.status === 'active' ? 'running' : campaign.status,
    suspendedReason: null,
  };
}
